import { useCallback, useEffect, useState } from "react";

/**
 * Theme switching hook, vendored from the former `@heroui/use-theme` (v2)
 * package. HeroUI v3 ships no theme hook or provider — its styles react to a
 * `.dark` class (or `[data-theme="dark"]`) on an ancestor, and switching is
 * left to the application. This owns that switching: it persists the choice
 * under `heroui-theme` and toggles the class on `<html>`, resolving `system`
 * to a concrete theme and tracking OS changes while in that mode.
 */
export const ThemeProps = {
  /** localStorage key holding the current theme */
  KEY: "heroui-theme",
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
} as const;

// `string & {}` accepts custom themes (and the loosely-typed
// `user.settings.theme`) while preserving autocomplete for the literals.
export type Theme =
  | typeof ThemeProps.LIGHT
  | typeof ThemeProps.DARK
  | typeof ThemeProps.SYSTEM
  | (string & {});

const MEDIA = "(prefers-color-scheme: dark)";

const getSystemTheme = (): Theme =>
  window.matchMedia?.(MEDIA).matches ? ThemeProps.DARK : ThemeProps.LIGHT;

/**
 * React hook to switch between themes.
 *
 * @param defaultTheme theme to use when nothing is stored (defaults to `system`)
 * @returns the current theme and a setter that persists and applies it
 */
export function useTheme(defaultTheme: Theme = ThemeProps.SYSTEM) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem(ThemeProps.KEY);
    if (storedTheme) return storedTheme;
    if (defaultTheme === ThemeProps.SYSTEM) return getSystemTheme();
    return defaultTheme;
  });

  // Persist the choice and toggle the class on <html>; no state update, so
  // this is safe to run from an effect. `system` resolves to a concrete theme.
  const applyTheme = useCallback((newTheme: Theme) => {
    const targetTheme =
      newTheme === ThemeProps.SYSTEM ? getSystemTheme() : newTheme;

    localStorage.setItem(ThemeProps.KEY, newTheme);
    document.documentElement.classList.remove(
      ThemeProps.LIGHT,
      ThemeProps.DARK,
      ThemeProps.SYSTEM,
    );
    document.documentElement.classList.add(targetTheme);
  }, []);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      applyTheme(newTheme);
      setThemeState(newTheme);
    },
    [applyTheme],
  );

  // Apply on mount (and on any theme change) so the DOM class matches the
  // resolved state. DOM-only — the initial theme comes from the useState
  // initializer, which doesn't touch the DOM.
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // While in `system` mode, follow OS changes.
  useEffect(() => {
    const media = window.matchMedia(MEDIA);
    const handleMediaQuery = (e: MediaQueryListEvent) => {
      if (defaultTheme === ThemeProps.SYSTEM) {
        setTheme(e.matches ? ThemeProps.DARK : ThemeProps.LIGHT);
      }
    };
    media.addEventListener("change", handleMediaQuery);
    return () => media.removeEventListener("change", handleMediaQuery);
  }, [defaultTheme, setTheme]);

  return { theme, setTheme };
}
